/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace geometric
*/
var geometric = {};

/**
* @name cdf
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/cdf}
*/
import cdf from './../../../../base/dists/geometric/cdf';
setReadOnly( geometric, 'cdf', cdf );

/**
* @name Geometric
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/ctor}
*/
import Geometric from './../../../../base/dists/geometric/ctor';
setReadOnly( geometric, 'Geometric', Geometric );

/**
* @name entropy
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/entropy}
*/
import entropy from './../../../../base/dists/geometric/entropy';
setReadOnly( geometric, 'entropy', entropy );

/**
* @name kurtosis
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/kurtosis}
*/
import kurtosis from './../../../../base/dists/geometric/kurtosis';
setReadOnly( geometric, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/logcdf}
*/
import logcdf from './../../../../base/dists/geometric/logcdf';
setReadOnly( geometric, 'logcdf', logcdf );

/**
* @name logpmf
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/logpmf}
*/
import logpmf from './../../../../base/dists/geometric/logpmf';
setReadOnly( geometric, 'logpmf', logpmf );

/**
* @name mean
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/mean}
*/
import mean from './../../../../base/dists/geometric/mean';
setReadOnly( geometric, 'mean', mean );

/**
* @name median
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/median}
*/
import median from './../../../../base/dists/geometric/median';
setReadOnly( geometric, 'median', median );

/**
* @name mgf
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/mgf}
*/
import mgf from './../../../../base/dists/geometric/mgf';
setReadOnly( geometric, 'mgf', mgf );

/**
* @name mode
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/mode}
*/
import mode from './../../../../base/dists/geometric/mode';
setReadOnly( geometric, 'mode', mode );

/**
* @name pmf
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/pmf}
*/
import pmf from './../../../../base/dists/geometric/pmf';
setReadOnly( geometric, 'pmf', pmf );

/**
* @name quantile
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/quantile}
*/
import quantile from './../../../../base/dists/geometric/quantile';
setReadOnly( geometric, 'quantile', quantile );

/**
* @name skewness
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/skewness}
*/
import skewness from './../../../../base/dists/geometric/skewness';
setReadOnly( geometric, 'skewness', skewness );

/**
* @name stdev
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/stdev}
*/
import stdev from './../../../../base/dists/geometric/stdev';
setReadOnly( geometric, 'stdev', stdev );

/**
* @name variance
* @memberof geometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/geometric/variance}
*/
import variance from './../../../../base/dists/geometric/variance';
setReadOnly( geometric, 'variance', variance );


// EXPORTS //

export default geometric;
